import React, { useState, useEffect } from "react";
import AthleteLayout from "../common/AthleteLayout";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import { Link, useParams } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import API_URL from "../../api/API";
import ladyRun from "../../images/ladyRun.png";

export const AthleteStaticStrengthTemplateDetail = () => {
  const { athlete, dispatch } = useAthleteAuthContext();
  const { id } = useParams();

  const stripe = useStripe();
  const elements = useElements();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isPurchased, setIsPurchased] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Fetch product & purchase data
  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        setLoading(true);

        // 1️⃣ Product detail
        const response = await fetch(
          `${API_URL.FRONTEND_STRENGTH_PRODUCT_DETAIL}${id}`,
          {
            headers: { Authorization: `Bearer ${athlete.token}` },
          },
        );
        if (!response.ok)
          throw new Error("Failed to fetch strength product details");
        const data = await response.json();
        if (!data?.data) throw new Error("Product not found");
        setProduct(data.data);

        // 2️⃣ Purchase data
        const purchaseResponse = await fetch(
          `${API_URL.GET_ATHLETE_PURCHASE_DATA}${athlete.id}`,
          {
            headers: { Authorization: `Bearer ${athlete.token}` },
          },
        );
        if (purchaseResponse.ok) {
          const purchaseData = await purchaseResponse.json();
          const purchased = purchaseData.data?.strength?.some(
            (item) =>
              String(item.template_id) === String(id) &&
              item.payment_status?.toUpperCase() === "COMPLETED",
          );
          if (purchased) setIsPurchased(true);
        }
      } catch (err) {
        setError(err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (athlete?.token && id) fetchProductDetail();
  }, [athlete?.token, id]);

  // Step 1: Show payment form
  const handleBuyClick = () => setShowPaymentForm(true);

  // Step 2: Confirm payment
  const handlePayment = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    if (!stripe || !elements) return;

    try {
      // 1️⃣ Create PaymentIntent
      const intentResponse = await fetch(API_URL.CREATE_PAYMENT_INTENT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${athlete.token}`,
        },
        body: JSON.stringify({
          amount: Math.round(product.price), // backend multiplies by 100
          currency: "usd",
          athlete_id: `${athlete.firstname} | ${athlete.email}`,
          template_id: product.title,
          category: "strength",
        }),
      });

      const { clientSecret } = await intentResponse.json();
      if (!clientSecret) throw new Error("Missing PaymentIntent client secret");

      // 2️⃣ Confirm payment
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (result.error) setError(result.error.message);
      else if (result.paymentIntent.status === "succeeded") {
        // 3️⃣ Save purchase record
        await fetch(API_URL.POST_ATHLETE_PURCHASE_STRENGTH_DATA + athlete.id, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${athlete.token}`,
          },
          body: JSON.stringify({
            template_id: product._id,
            price: product.price,
            payment_transaction_id: result.paymentIntent.id,
            payment_logs: "Stripe inline payment success",
            payment_status: "COMPLETED",
          }),
        });

        setSuccessMessage("Payment successful! You now have access.");
        setIsPurchased(true);
        setShowPaymentForm(false);

        // 4️⃣ Update athlete context
        // 4️⃣ Update athlete account type ONLY if NOT already AMAX COACHED
        if (athlete.account_type?.toUpperCase() !== "AMAX COACHED") {
          dispatch({ type: "UPDATE_ACCOUNT_TYPE", payload: "STATIC PLAN" });

          localStorage.setItem(
            "athlete",
            JSON.stringify({
              ...athlete,
              account_type: "STATIC PLAN",
            }),
          );
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Helper to extract YouTube ID
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/(?:.*v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    );
    return match ? match[1] : null;
  };

  const videoId = extractYouTubeId(product?.youtube_url);

  return (
    <AthleteLayout>
      <div className="bg-white h-full overflow-y-auto">
        {loading ? (
          <div className="text-center py-10">Loading program details...</div>
        ) : error ? (
          <div className="text-center py-10 text-red-500">Error: {error}</div>
        ) : (
          product && (
            <div className="p-5 w-full max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl overflow-hidden relative">
                <Link
                  to="/athlete/purchase/static-strength-templates"
                  className="absolute top-3 left-3 bg-white size-8 p-2 rounded-full flex justify-center items-center hover:bg-gray-100 transition-all z-10"
                >
                  <ArrowL />
                </Link>

                {videoId ? (
                  <div className="bg-smock rounded-2xl p-5 mx-auto relative shadow-sm">
                    <iframe
                      className="w-full aspect-video sm:h-80 object-cover rounded-2xl"
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title="Workout video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="bg-smock rounded-2xl p-5 mx-auto relative shadow-sm">
                    <img
                      src={
                        product?.image
                          ? `${API_URL.SERVER_PICTURE_URL}${product.image}`
                          : ladyRun
                      }
                      alt={product?.title || "Strength Program"}
                      className="w-full aspect-video sm:h-80 object-cover rounded-2xl"
                    />
                  </div>
                )}
              </div>

              <div className="mt-6">
                <div className="space-y-2">
                  <p className="text-black text-xl">{product?.title}</p>
                  <p className="text-black text-base font-light mt-2">
                    ${product?.price}
                  </p>
                  <div className="bg-smock rounded-2xl p-5">
                    <p className="text-black text-base font-light leading-relaxed whitespace-pre-line">
                      {product?.info || "No description available."}
                    </p>
                  </div>
                </div>
              </div>

              {successMessage && (
                <div className="bg-green-500 text-white p-4 rounded-md mt-4 text-center">
                  {successMessage}
                </div>
              )}

              <div className="w-full mx-auto mt-8">
                {showPaymentForm ? (
                  <form onSubmit={handlePayment} className="space-y-4">
                    <CardElement className="p-4 border rounded-md bg-white" />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <button
                      type="submit"
                      disabled={!stripe || saving}
                      className="p-4 bg-black text-white rounded-full w-full disabled:opacity-50"
                    >
                      {saving ? "Processing..." : "Pay Now"}
                    </button>
                  </form>
                ) : isPurchased ? (
                  <Link
                    to={`/athlete/strength-products`}
                    className="text-center w-full p-3 xs:p-4 text-white text-base leading-none bg-black hover:bg-black/70 rounded-xl transition-all duration-300 block"
                  >
                    Access Now
                  </Link>
                ) : (
                  <button
                    onClick={handleBuyClick}
                    className="text-center w-full p-3 xs:p-4 text-white text-base leading-none bg-black hover:bg-black/70 rounded-xl transition-all duration-300 block"
                  >
                    Buy Now
                  </button>
                )}
              </div>
            </div>
          )
        )}
      </div>
    </AthleteLayout>
  );
};

// Arrow back icon
const ArrowL = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="14"
    viewBox="0 0 8 14"
    fill="none"
  >
    <path
      d="M7 13L1 7L7 1"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
